<template>
  <AdminLayout>


      <!-- OVERVIEW -->
      <div v-if="activeTab === 'overview'">
        <h2 class="dash-heading">Dashboard Overview</h2>

        <div v-if="statsLoading" class="dash-loading">Loading stats...</div>
        <template v-else>
          <div class="dash-stats-row">
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(201,14,14,0.15)">
                <i class="ti ti-users" style="color:#c90e0e"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ stats.total_users }}</div>
                <div class="dash-stat-label">Total Users</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(25,135,84,0.15)">
                <i class="ti ti-circle-check" style="color:#4ade80"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ stats.active_subs }}</div>
                <div class="dash-stat-label">Active Subscriptions</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(251,191,36,0.15)">
                <i class="ti ti-clock" style="color:#fbbf24"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ stats.pending_lines }}</div>
                <div class="dash-stat-label">Pending Lines</div>
              </div>
            </div>
            <div class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(96,165,250,0.15)">
                <i class="ti ti-coin" style="color:#60a5fa"></i>
              </div>
              <div>
                <div class="dash-stat-value">${{ money(stats.total_revenue) }}</div>
                <div class="dash-stat-label">Total Revenue</div>
              </div>
            </div>
            <div v-if="panelCredits" class="dash-stat-card">
              <div class="dash-stat-icon" style="background:rgba(168,85,247,0.15)">
                <i class="ti ti-server" style="color:#a855f7"></i>
              </div>
              <div>
                <div class="dash-stat-value">{{ panelCredits.credits }}</div>
                <div class="dash-stat-label">Panel Credits</div>
              </div>
            </div>
          </div>

          <div class="row g-4">
            <!-- Recent Users -->
            <div class="col-lg-7">
              <div class="dash-card h-100">
                <div class="dash-card-header">
                  <h5 class="dash-card-title mb-0">Recent Users</h5>
                  <button class="dash-link-btn" @click="activeTab = 'users'">View All</button>
                </div>
                <div class="dash-table-wrap">
                  <table class="dash-table">
                    <thead>
                      <tr><th>Name</th><th>Email</th><th>Status</th><th>Joined</th></tr>
                    </thead>
                    <tbody>
                      <tr v-for="u in stats.recent_users" :key="u.id">
                        <td>
                          <div class="dash-user-cell">
                            <div class="dash-mini-avatar">{{ u.name?.[0]?.toUpperCase() }}</div>
                            {{ u.name }}
                          </div>
                        </td>
                        <td class="text-silver">{{ u.email }}</td>
                        <td>
                          <span class="dash-badge" :class="u.status === 'active' ? 'badge-green' : u.status === 'none' ? 'badge-gray' : 'badge-orange'">
                            {{ u.status }}
                          </span>
                        </td>
                        <td class="text-silver">{{ u.joined }}</td>
                      </tr>
                      <tr v-if="!stats.recent_users?.length">
                        <td colspan="4" class="text-center text-silver py-3">No users yet</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Pending Lines -->
            <div class="col-lg-5">
              <div class="dash-card h-100">
                <div class="dash-card-header">
                  <h5 class="dash-card-title mb-0">Pending Lines</h5>
                  <span class="dash-badge badge-orange" v-if="stats.pending_lines > 0">{{ stats.pending_lines }}</span>
                </div>
                <div v-if="!stats.pending_lines_list?.length" class="text-center py-4">
                  <i class="ti ti-circle-check" style="font-size:2rem;color:#4ade80;display:block;margin-bottom:0.5rem"></i>
                  <p class="text-silver mb-0" style="font-size:0.9rem">All lines assigned</p>
                </div>
                <div v-else>
                  <div v-for="item in stats.pending_lines_list" :key="item.id" class="dash-pending-item">
                    <div class="dash-mini-avatar">{{ item.name?.[0]?.toUpperCase() }}</div>
                    <div style="flex:1;min-width:0">
                      <div style="color:#fff;font-size:0.9rem;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">{{ item.name }}</div>
                      <div class="text-silver" style="font-size:0.78rem">{{ item.email }}</div>
                    </div>
                    <button class="dash-action-btn" @click="openAssignModal(item)">Assign</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- USERS -->
      <div v-if="activeTab === 'users'">
        <div class="dash-page-header">
          <h2 class="dash-heading mb-0">Users</h2>
          <div class="d-flex gap-2 align-items-center flex-wrap">
            <input v-model="userSearch" @keyup.enter="loadUsers" type="text" class="dash-input dash-search" placeholder="Search by name or email..." />
            <button class="btn btn-sm" @click="loadUsers"><span class="border-style">Search</span></button>
            <button class="btn btn-sm" @click="openBillModal()"><span class="border-style"><i class="ti ti-credit-card"></i> Bill Client</span></button>
          </div>
        </div>

        <div v-if="usersLoading" class="dash-loading">Loading users...</div>
        <div v-else class="dash-card">
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr><th>User</th><th>Email</th><th>Phone</th><th>Subscription</th><th>Line</th><th>Joined</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="u in users" :key="u.id">
                  <td>
                    <div class="dash-user-cell">
                      <div class="dash-mini-avatar">{{ u.name?.[0]?.toUpperCase() }}</div>
                      {{ u.name }}
                    </div>
                  </td>
                  <td class="text-silver">{{ u.email }}</td>
                  <td class="text-silver">{{ u.phone || '—' }}</td>
                  <td>
                    <span v-if="u.subscriptions?.length" class="dash-badge"
                      :class="u.subscriptions[0].status === 'active' ? 'badge-green' : 'badge-orange'">
                      {{ u.subscriptions[0].package?.name }}
                    </span>
                    <span v-else class="dash-badge badge-gray">None</span>
                  </td>
                  <td>
                    <span v-if="u.iptv_lines?.length" class="dash-badge"
                      :class="u.iptv_lines[0].status === 'assigned' ? 'badge-green' : 'badge-orange'">
                      {{ u.iptv_lines[0].status }}
                    </span>
                    <span v-else class="dash-badge badge-gray">None</span>
                  </td>
                  <td class="text-silver" style="font-size:0.82rem">{{ formatDate(u.created_at) }}</td>
                  <td>
                    <div class="d-flex gap-2 flex-wrap">
                      <button class="dash-action-btn" style="border-color:#4ade80;color:#4ade80" @click="openBillModal(u)">
                        <i class="ti ti-credit-card"></i> Bill
                      </button>
                      <button class="dash-action-btn" @click="openAssignModal({ user_id: u.id, name: u.name, email: u.email })">Assign Line</button>
                      <button class="dash-action-btn" style="border-color:#60a5fa;color:#60a5fa" @click="openEmailModal(u)">
                        <i class="ti ti-mail"></i> Email
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!users.length">
                  <td colspan="7" class="text-center text-silver py-3">No users found</td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-silver" style="font-size:0.85rem">Page {{ usersMeta.current_page }} of {{ usersMeta.last_page }}</span>
            <div class="d-flex gap-2">
              <button class="dash-action-btn" :disabled="usersMeta.current_page <= 1" @click="loadUsers(usersMeta.current_page - 1)">Prev</button>
              <button class="dash-action-btn" :disabled="usersMeta.current_page >= usersMeta.last_page" @click="loadUsers(usersMeta.current_page + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>

      <!-- EMAILS -->
      <div v-if="activeTab === 'emails'">
        <div class="dash-page-header">
          <h2 class="dash-heading mb-0">Email Centre</h2>
          <button class="btn" @click="openCompose()">
            <span class="border-style"><i class="ti ti-plus"></i> Compose</span>
          </button>
        </div>

        <!-- Compose panel -->
        <div v-if="compose.open" class="dash-card mb-4">
          <div class="dash-card-header">
            <h5 class="dash-card-title mb-0"><i class="ti ti-send" style="color:#60a5fa;margin-right:0.4rem"></i>New Email</h5>
            <button class="dash-link-btn" style="color:#888" @click="compose.open = false">✕ Close</button>
          </div>

          <div v-if="compose.error" class="dash-alert dash-alert-danger mb-3">{{ compose.error }}</div>
          <div v-if="compose.success" class="dash-alert dash-alert-success mb-3">{{ compose.success }}</div>

          <!-- Recipient -->
          <div class="dash-field-group mb-3" style="position:relative">
            <label class="dash-field-label">To — type name, email, or select a user</label>
            <div class="compose-to-row">
              <input
                v-model="compose.userQuery"
                type="text"
                class="dash-input"
                placeholder="e.g. john@example.com  or  John Doe"
                :style="compose.to_email ? 'border-color:#4ade80' : ''"
                @input="onUserQuery"
                @keydown.esc="compose.userResults = []"
                autocomplete="off"
              />
              <button v-if="compose.to_email" class="compose-clear-btn" @click="clearUser" title="Clear recipient">✕</button>
            </div>
            <div v-if="compose.to_email" class="compose-resolved">
              <i class="ti ti-circle-check" style="color:#4ade80"></i>
              {{ compose.to_name ? compose.to_name + ' — ' : '' }}{{ compose.to_email }}
            </div>
            <!-- Dropdown results -->
            <div v-if="compose.userResults.length" class="compose-dropdown">
              <div
                v-for="u in compose.userResults"
                :key="u.id"
                class="compose-dropdown-item"
                @mousedown.prevent="selectUser(u)"
              >
                <div class="dash-mini-avatar" style="width:28px;height:28px;font-size:0.75rem;flex-shrink:0">{{ u.name?.[0]?.toUpperCase() }}</div>
                <div style="flex:1;min-width:0">
                  <div style="color:#fff;font-size:0.88rem;font-weight:600">{{ u.name }}</div>
                  <div style="color:#888;font-size:0.78rem">{{ u.email }}{{ u.phone ? '  ·  ' + u.phone : '' }}</div>
                </div>
              </div>
              <div v-if="compose.userSearching" class="compose-dropdown-item" style="color:#888;font-size:0.82rem">Searching...</div>
            </div>
          </div>

          <div class="dash-field-group mb-3">
            <label class="dash-field-label">Subject</label>
            <input v-model="compose.subject" type="text" class="dash-input" placeholder="e.g. Your GoPlayTV subscription is active" />
          </div>

          <div class="dash-field-group mb-4">
            <label class="dash-field-label">Message</label>
            <textarea
              v-model="compose.body"
              class="dash-input"
              rows="8"
              style="height:auto;resize:vertical;font-family:inherit"
              placeholder="Write your message here..."
            ></textarea>
          </div>

          <div class="d-flex gap-2">
            <button class="btn" :disabled="compose.sending" @click="submitCompose">
              <span class="border-style">
                <i class="ti ti-send"></i>
                {{ compose.sending ? 'Sending...' : 'Send Email' }}
              </span>
            </button>
            <button class="dash-action-btn" @click="compose.open = false">Cancel</button>
          </div>
        </div>

        <!-- Sent email log -->
        <div class="dash-page-header" style="margin-bottom:1rem">
          <div class="d-flex gap-2 align-items-center">
            <input v-model="emailLogSearch" @keyup.enter="loadEmails()" type="text" class="dash-input dash-search" placeholder="Search by email, name, or subject..." />
            <button class="btn btn-sm" @click="loadEmails()"><span class="border-style">Search</span></button>
          </div>
        </div>

        <div v-if="emailsLoading" class="dash-loading">Loading emails...</div>
        <div v-else-if="!emailLog.length" class="dash-card text-center py-5">
          <i class="ti ti-mail-off" style="font-size:2.5rem;color:#555;display:block;margin-bottom:0.75rem"></i>
          <p class="text-silver mb-0">No emails sent yet. Hit Compose to send your first.</p>
        </div>
        <div v-else class="dash-card">
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr><th>To</th><th>Subject</th><th>Status</th><th>Sent</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="em in emailLog" :key="em.id">
                  <td>
                    <div class="dash-user-cell">
                      <div class="dash-mini-avatar">{{ (em.to_name || em.to_email)?.[0]?.toUpperCase() }}</div>
                      <div>
                        <div style="color:#fff;font-size:0.88rem">{{ em.to_name || '—' }}</div>
                        <div class="text-silver" style="font-size:0.78rem">{{ em.to_email }}</div>
                      </div>
                    </div>
                  </td>
                  <td style="color:#fff;max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ em.subject }}</td>
                  <td>
                    <span class="dash-badge" :class="em.status === 'sent' ? 'badge-green' : 'badge-red'">
                      {{ em.status }}
                    </span>
                  </td>
                  <td class="text-silver" style="font-size:0.82rem;white-space:nowrap">{{ formatDate(em.created_at) }}</td>
                  <td>
                    <button class="dash-action-btn" style="border-color:#60a5fa;color:#60a5fa"
                      @click="openCompose({ email: em.to_email, name: em.to_name, user_id: em.user_id })">
                      <i class="ti ti-send"></i> Reply
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-silver" style="font-size:0.85rem">Page {{ emailLogMeta.current_page }} of {{ emailLogMeta.last_page }}</span>
            <div class="d-flex gap-2">
              <button class="dash-action-btn" :disabled="emailLogMeta.current_page <= 1" @click="loadEmails(emailLogMeta.current_page - 1)">Prev</button>
              <button class="dash-action-btn" :disabled="emailLogMeta.current_page >= emailLogMeta.last_page" @click="loadEmails(emailLogMeta.current_page + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>

      <!-- PAYMENTS -->
      <div v-if="activeTab === 'payments'">
        <h2 class="dash-heading">Payments</h2>

        <!-- Payment stats row -->
        <div class="dash-stats-row" style="margin-bottom:1.5rem">
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(96,165,250,0.15)">
              <i class="ti ti-coin" style="color:#60a5fa"></i>
            </div>
            <div>
              <div class="dash-stat-value">${{ money(paymentStats.total_revenue) }}</div>
              <div class="dash-stat-label">Total Revenue</div>
            </div>
          </div>
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(25,135,84,0.15)">
              <i class="ti ti-circle-check" style="color:#4ade80"></i>
            </div>
            <div>
              <div class="dash-stat-value">{{ paymentStats.completed }}</div>
              <div class="dash-stat-label">Completed</div>
            </div>
          </div>
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(251,191,36,0.15)">
              <i class="ti ti-clock" style="color:#fbbf24"></i>
            </div>
            <div>
              <div class="dash-stat-value">{{ paymentStats.pending }}</div>
              <div class="dash-stat-label">Pending</div>
            </div>
          </div>
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(201,14,14,0.15)">
              <i class="ti ti-x" style="color:#f87171"></i>
            </div>
            <div>
              <div class="dash-stat-value">{{ paymentStats.failed }}</div>
              <div class="dash-stat-label">Failed / Cancelled</div>
            </div>
          </div>
        </div>

        <!-- Filter -->
        <div class="dash-page-header" style="margin-bottom:1rem">
          <div class="d-flex gap-2 flex-wrap">
            <button class="dash-action-btn" :class="{ 'active-filter': paymentStatusFilter === '' }" @click="paymentStatusFilter = ''; loadPayments()">All</button>
            <button class="dash-action-btn" :class="{ 'active-filter': paymentStatusFilter === 'completed' }" @click="paymentStatusFilter = 'completed'; loadPayments()">Completed</button>
            <button class="dash-action-btn" :class="{ 'active-filter': paymentStatusFilter === 'pending' }" @click="paymentStatusFilter = 'pending'; loadPayments()">Pending</button>
            <button class="dash-action-btn" :class="{ 'active-filter': paymentStatusFilter === 'failed' }" @click="paymentStatusFilter = 'failed'; loadPayments()">Failed</button>
          </div>
        </div>

        <div v-if="paymentsLoading" class="dash-loading">Loading payments...</div>
        <div v-else class="dash-card">
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Package</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Invoice</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="p in payments" :key="p.id">
                  <td>
                    <div class="dash-user-cell">
                      <div class="dash-mini-avatar">{{ p.user?.name?.[0]?.toUpperCase() }}</div>
                      <div>
                        <div>{{ p.user?.name || '—' }}</div>
                        <div class="text-silver" style="font-size:0.75rem">{{ p.user?.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="text-silver" style="white-space:nowrap">
                    {{ p.package?.name || '—' }}<br/>
                    <span style="font-size:0.75rem">{{ p.package?.duration_days ? p.package.duration_days + 'd' : '' }}</span>
                  </td>
                  <td style="color:#4ade80;font-weight:700">${{ p.amount }}</td>
                  <td>
                    <span class="dash-badge"
                      :class="{
                        'badge-green':  p.status === 'completed',
                        'badge-orange': p.status === 'pending',
                        'badge-red':    p.status === 'failed' || p.status === 'cancelled',
                      }">
                      {{ p.status }}
                    </span>
                  </td>
                  <td class="text-silver" style="font-size:0.78rem;max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                    {{ p.invoice_id || '—' }}
                  </td>
                  <td class="text-silver" style="font-size:0.82rem;white-space:nowrap">{{ formatDate(p.created_at) }}</td>
                  <td>
                    <div class="d-flex gap-2 flex-wrap">
                      <button
                        v-if="p.checkout_url && p.status === 'pending'"
                        class="dash-action-btn"
                        @click="openInvoiceDialog(p.checkout_url)"
                      >
                        <i class="ti ti-external-link"></i> Invoice
                      </button>
                      <button
                        v-if="p.status === 'pending'"
                        class="dash-action-btn"
                        style="border-color:#4ade80;color:#4ade80"
                        :disabled="markingPaid === p.id"
                        @click="markAsPaid(p.id)"
                      >
                        {{ markingPaid === p.id ? '...' : '✓ Paid' }}
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!payments.length">
                  <td colspan="7" class="text-center text-silver py-3">No payments yet</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-silver" style="font-size:0.85rem">Page {{ paymentsMeta.current_page }} of {{ paymentsMeta.last_page }}</span>
            <div class="d-flex gap-2">
              <button class="dash-action-btn" :disabled="paymentsMeta.current_page <= 1" @click="loadPayments(paymentsMeta.current_page - 1)">Prev</button>
              <button class="dash-action-btn" :disabled="paymentsMeta.current_page >= paymentsMeta.last_page" @click="loadPayments(paymentsMeta.current_page + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>

      <!-- PACKAGES -->
      <div v-if="activeTab === 'packages'">
        <div class="dash-page-header">
          <h2 class="dash-heading mb-0">Packages</h2>
          <button class="btn" @click="openPackageModal()"><span class="border-style">+ Add Package</span></button>
        </div>

        <div v-if="pkgsLoading" class="dash-loading">Loading packages...</div>
        <div v-else class="dash-card">
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr><th>Image</th><th>Name</th><th>Price</th><th>Duration</th><th>Panel</th><th>Features</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="p in packages" :key="p.id">
                  <td style="width:64px;padding:0.4rem 0.75rem">
                    <div class="pkg-thumb-wrap">
                      <img v-if="p.image_url" :src="p.image_url" :alt="p.name" class="pkg-thumb" />
                      <div v-else class="pkg-thumb-empty"><i class="ti ti-photo"></i></div>
                    </div>
                  </td>
                  <td style="color:#fff;font-weight:600">{{ p.name }}</td>
                  <td style="color:#c90e0e;font-weight:700">${{ p.price }}</td>
                  <td class="text-silver">{{ p.duration_days }} days</td>
                  <td>
                    <span v-if="p.panel_package_id" class="dash-badge badge-green" :title="'Panel package #' + p.panel_package_id">
                      #{{ p.panel_package_id }}
                    </span>
                    <span v-else class="dash-badge badge-gray" title="No panel mapping — lines must be assigned manually">manual</span>
                  </td>
                  <td class="text-silver" style="font-size:0.82rem;max-width:200px">
                    <span v-if="Array.isArray(p.features)">{{ p.features.join(', ') }}</span>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <div class="d-flex gap-2">
                      <button class="dash-action-btn" @click="openPackageModal(p)">Edit</button>
                      <button class="dash-action-btn dash-action-danger" @click="deletePackage(p.id)">Delete</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!packages.length">
                  <td colspan="7" class="text-center text-silver py-3">No packages yet</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- IPTV PANEL -->
      <div v-if="activeTab === 'panel'">
        <div class="dash-page-header">
          <h2 class="dash-heading mb-0">IPTV Panel</h2>
          <button class="dash-action-btn" @click="reloadPanelTab">
            <i class="ti ti-refresh"></i> Reload
          </button>
        </div>

        <!-- Not connected warning -->
        <div v-if="!panelCredits" class="dash-card mb-4" style="border-left:3px solid #fbbf24">
          <div class="d-flex align-items-center gap-3">
            <i class="ti ti-plug-x" style="font-size:1.8rem;color:#fbbf24"></i>
            <div>
              <div style="color:#fff;font-weight:700">Panel API not connected</div>
              <p class="text-silver mb-0" style="font-size:0.85rem">
                Set <code>PANEL_API_KEY</code> in the backend <code>.env</code> and make sure this server's IP is whitelisted on the panel.
                Until then, lines must be assigned manually.
              </p>
            </div>
          </div>
        </div>

        <!-- Reseller account -->
        <div v-else class="dash-stats-row" style="margin-bottom:1.5rem">
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(168,85,247,0.15)">
              <i class="ti ti-coin" style="color:#a855f7"></i>
            </div>
            <div>
              <div class="dash-stat-value">{{ panelCredits.credits }}</div>
              <div class="dash-stat-label">Credits Remaining</div>
            </div>
          </div>
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(96,165,250,0.15)">
              <i class="ti ti-user" style="color:#60a5fa"></i>
            </div>
            <div>
              <div class="dash-stat-value" style="font-size:1.1rem">{{ panelCredits.username }}</div>
              <div class="dash-stat-label">Reseller Account</div>
            </div>
          </div>
          <div class="dash-stat-card">
            <div class="dash-stat-icon" style="background:rgba(74,222,128,0.15)">
              <i class="ti ti-world" style="color:#4ade80"></i>
            </div>
            <div>
              <div class="dash-stat-value" style="font-size:1.1rem">{{ panelCredits.reseller_dns || '—' }}</div>
              <div class="dash-stat-label">Reseller DNS</div>
            </div>
          </div>
        </div>

        <!-- Lines -->
        <div class="dash-card mb-4">
          <div class="dash-card-header">
            <h5 class="dash-card-title mb-0">Customer Lines</h5>
            <div class="d-flex gap-2">
              <button class="dash-action-btn" :class="{ 'active-filter': panelLineFilter === '' }" @click="panelLineFilter = ''; loadPanelLines()">All</button>
              <button class="dash-action-btn" :class="{ 'active-filter': panelLineFilter === 'active' }" @click="panelLineFilter = 'active'; loadPanelLines()">Active</button>
              <button class="dash-action-btn" :class="{ 'active-filter': panelLineFilter === 'pending_assignment' }" @click="panelLineFilter = 'pending_assignment'; loadPanelLines()">Pending</button>
            </div>
          </div>
          <div v-if="panelLinesLoading" class="dash-loading">Loading lines...</div>
          <div v-else class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr><th>User</th><th>Status</th><th>Type</th><th>Identifier</th><th>Expires</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="l in panelLines" :key="l.id">
                  <td>
                    <div class="dash-user-cell">
                      <div class="dash-mini-avatar">{{ l.user?.name?.[0]?.toUpperCase() }}</div>
                      <div>
                        <div style="color:#fff;font-size:0.88rem">{{ l.user?.name || '—' }}</div>
                        <div class="text-silver" style="font-size:0.75rem">{{ l.user?.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="dash-badge" :class="l.status === 'active' ? 'badge-green' : 'badge-orange'">
                      {{ l.status === 'pending_assignment' ? 'pending' : l.status }}
                    </span>
                  </td>
                  <td>
                    <span v-if="l.panel_type" class="dash-badge" style="background:rgba(168,85,247,0.15);color:#a855f7;text-transform:uppercase">{{ l.panel_type }}</span>
                    <span v-else class="dash-badge badge-gray">manual</span>
                  </td>
                  <td class="text-silver" style="font-size:0.82rem;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                    {{ l.username || l.mac_address || '—' }}
                  </td>
                  <td class="text-silver" style="font-size:0.82rem;white-space:nowrap">
                    {{ l.expires_at ? formatDate(l.expires_at) : '—' }}
                  </td>
                  <td>
                    <button class="dash-action-btn" @click="openAssignModal({ user_id: l.user_id, name: l.user?.name, mac_address: l.mac_address })">
                      Manage
                    </button>
                  </td>
                </tr>
                <tr v-if="!panelLines.length">
                  <td colspan="6" class="text-center text-silver py-3">No lines yet</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-silver" style="font-size:0.85rem">Page {{ panelLinesMeta.current_page }} of {{ panelLinesMeta.last_page }}</span>
            <div class="d-flex gap-2">
              <button class="dash-action-btn" :disabled="panelLinesMeta.current_page <= 1" @click="loadPanelLines(panelLinesMeta.current_page - 1)">Prev</button>
              <button class="dash-action-btn" :disabled="panelLinesMeta.current_page >= panelLinesMeta.last_page" @click="loadPanelLines(panelLinesMeta.current_page + 1)">Next</button>
            </div>
          </div>
        </div>

        <!-- Panel packages + mapping -->
        <div class="dash-card">
          <div class="dash-card-header">
            <h5 class="dash-card-title mb-0">Panel Packages & Mapping</h5>
            <button class="dash-link-btn" @click="activeTab = 'packages'">Edit Packages</button>
          </div>
          <p class="text-silver" style="font-size:0.85rem;margin-top:-0.5rem">
            Map each GoPlayTV package to a panel package (edit the package and pick from the dropdown) so paid customers get provisioned automatically.
          </p>
          <div v-if="!panelPackages.length" class="text-silver py-3" style="font-size:0.9rem">
            <i class="ti ti-plug-x"></i> Panel packages unavailable — connect the panel API first.
          </div>
          <div v-else class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr><th>Panel ID</th><th>Panel Package</th><th>Cost</th><th>Duration</th><th>Connections</th><th>Mapped Local Package</th></tr>
              </thead>
              <tbody>
                <tr v-for="pp in panelPackages" :key="pp.id">
                  <td style="color:#a855f7;font-weight:700">#{{ pp.id }}</td>
                  <td style="color:#fff;font-weight:600">{{ pp.name }}</td>
                  <td class="text-silver">{{ pp.credits }} credits</td>
                  <td class="text-silver">{{ pp.duration }} {{ pp.duration_in }}</td>
                  <td class="text-silver">{{ pp.connections }}</td>
                  <td>
                    <span v-if="mappedLocalPackage(pp.id)" class="dash-badge badge-green">{{ mappedLocalPackage(pp.id) }}</span>
                    <span v-else class="dash-badge badge-gray">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- CHANNELS -->
      <div v-if="activeTab === 'channels'">
        <div class="dash-page-header">
          <h2 class="dash-heading mb-0">Channels</h2>
          <button class="btn" @click="openChannelModal()"><span class="border-style">+ Add Channel</span></button>
        </div>

        <!-- Search + category filter -->
        <div class="d-flex gap-2 flex-wrap mb-3">
          <input v-model="channelSearch" @keyup.enter="loadChannels" type="text" class="dash-input dash-search" placeholder="Search channels..." />
          <button class="btn btn-sm" @click="loadChannels"><span class="border-style">Search</span></button>
        </div>

        <div v-if="channelsLoading" class="dash-loading">Loading channels...</div>
        <div v-else class="dash-card">
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr><th>Logo</th><th>Name</th><th>Category</th><th>Country</th><th>Active</th><th>Actions</th></tr>
              </thead>
              <tbody>
                <tr v-for="ch in channels" :key="ch.id">
                  <td style="width:64px;padding:0.4rem 0.75rem">
                    <div class="pkg-thumb-wrap">
                      <img v-if="ch.logo_url" :src="ch.logo_url" :alt="ch.name" class="pkg-thumb" style="object-fit:contain;background:#0a0102" />
                      <div v-else class="pkg-thumb-empty"><i class="ti ti-tv"></i></div>
                    </div>
                  </td>
                  <td style="color:#fff;font-weight:600">{{ ch.name }}</td>
                  <td><span class="dash-badge badge-orange" style="text-transform:none">{{ ch.category }}</span></td>
                  <td class="text-silver">{{ ch.country || '—' }}</td>
                  <td>
                    <span class="dash-badge" :class="ch.is_active ? 'badge-green' : 'badge-gray'">
                      {{ ch.is_active ? 'Active' : 'Hidden' }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex gap-2">
                      <button class="dash-action-btn" @click="openChannelModal(ch)">Edit</button>
                      <button class="dash-action-btn dash-action-danger" @click="deleteChannel(ch.id)">Delete</button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!channels.length">
                  <td colspan="6" class="text-center text-silver py-3">No channels yet. Add your first channel above.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-silver" style="font-size:0.85rem">Page {{ channelsMeta.current_page }} of {{ channelsMeta.last_page }}</span>
            <div class="d-flex gap-2">
              <button class="dash-action-btn" :disabled="channelsMeta.current_page <= 1" @click="loadChannels(channelsMeta.current_page - 1)">Prev</button>
              <button class="dash-action-btn" :disabled="channelsMeta.current_page >= channelsMeta.last_page" @click="loadChannels(channelsMeta.current_page + 1)">Next</button>
            </div>
          </div>
        </div>
      </div>

      <!-- WEBSITE CMS -->
      <div v-if="activeTab === 'content'">
        <h2 class="dash-heading">Website CMS</h2>
        <p class="text-silver mb-4" style="margin-top:-1rem;font-size:0.95rem">
          Edit every section of your landing page, navbar, and footer here.
        </p>

        <div v-if="cmsLoading" class="dash-loading">Loading sections...</div>

        <div v-else class="row g-3">
          <div v-for="block in cmsSections" :key="block.section" class="col-md-6 col-lg-4">
            <div class="dash-card h-100 d-flex flex-column justify-content-between" style="padding:1.25rem">
              <div>
                <div class="d-flex align-items-center gap-2 mb-1">
                  <i :class="cmsIcon(block.section)" style="color:#c90e0e;font-size:1.1rem"></i>
                  <h5 class="text-white mb-0" style="font-size:0.95rem;font-weight:700">
                    {{ CMS_LABELS[block.section] || block.section.replace(/_/g,' ') }}
                  </h5>
                </div>
                <p class="text-silver mb-0" style="font-size:0.78rem">
                  Updated {{ formatDate(block.updated_at) }}
                </p>
              </div>
              <RouterLink :to="'/admin/content/' + block.section" class="btn mt-3 d-block text-center">
                <span class="border-style">Edit Content</span>
              </RouterLink>
            </div>
          </div>

          <div v-if="!cmsSections.length" class="col-12">
            <div class="dash-card text-center py-4">
              <p class="text-silver mb-0">No content sections found. Run the database seeder first.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- SETTINGS -->
      <div v-if="activeTab === 'settings'">
        <h2 class="dash-heading">Settings</h2>

        <div v-if="settingsLoading" class="dash-loading">Loading settings...</div>
        <template v-else>

          <!-- Save bar -->
          <div v-if="settingsMsg" class="dash-alert dash-alert-success mb-4">{{ settingsMsg }}</div>
          <div v-if="settingsError" class="dash-alert dash-alert-danger mb-4">{{ settingsError }}</div>

          <!-- Email / SMTP -->
          <div class="dash-card mb-4">
            <div class="dash-card-header">
              <h5 class="dash-card-title mb-0"><i class="ti ti-mail" style="color:#60a5fa;margin-right:0.5rem"></i>Email / SMTP</h5>
            </div>

            <div class="row g-3">
              <template v-for="s in settingsByGroup('email')" :key="s.key">
                <div class="col-md-6">
                  <label class="dash-field-label">
                    {{ s.label }}
                    <span v-if="s.is_secret" class="st-secret-badge">secret</span>
                  </label>
                  <input
                    v-model="settingsDraft[s.key]"
                    :type="s.is_secret ? 'password' : 'text'"
                    class="dash-input"
                    :placeholder="s.is_secret ? 'Enter to change' : s.label"
                    autocomplete="off"
                  />
                </div>
              </template>
            </div>

            <!-- Test email -->
            <div class="st-test-row mt-4">
              <div class="st-test-label">
                <i class="ti ti-send" style="color:#60a5fa"></i>
                Send a test email to verify SMTP
              </div>
              <div class="st-test-inputs">
                <input v-model="testMailTo" type="email" class="dash-input" placeholder="your@email.com" style="flex:1;min-width:200px" />
                <button class="dash-action-btn" style="border-color:#60a5fa;color:#60a5fa;white-space:nowrap"
                  :disabled="testMailSending || !testMailTo"
                  @click="sendTestMail">
                  <i class="ti ti-send"></i> {{ testMailSending ? 'Sending...' : 'Send Test' }}
                </button>
              </div>
              <div v-if="testMailMsg"   class="st-test-result st-ok">{{ testMailMsg }}</div>
              <div v-if="testMailError" class="st-test-result st-err">{{ testMailError }}</div>
            </div>
          </div>

          <!-- Payments / Card gateway -->
          <div class="dash-card mb-4">
            <div class="dash-card-header">
              <h5 class="dash-card-title mb-0"><i class="ti ti-credit-card" style="color:#4ade80;margin-right:0.5rem"></i>Payments</h5>
            </div>
            <p class="text-silver mb-3" style="font-size:0.88rem">
              Choose which gateway handles the <strong>Pay by Card</strong> button. Provider credentials live in the backend
              <code>.env</code>; a gateway shown as <em>not configured</em> can't be selected until its keys are set.
            </p>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="dash-field-label">Active Card Gateway</label>
                <select v-model="settingsDraft['card_gateway']" class="dash-input">
                  <option v-for="g in gateways" :key="g.key" :value="g.key" :disabled="!g.configured">
                    {{ g.label }}{{ g.configured ? '' : ' — not configured' }}
                  </option>
                </select>
              </div>
            </div>
            <div v-if="gateways.length" class="d-flex gap-2 flex-wrap mt-3">
              <span v-for="g in gateways" :key="g.key" class="dash-badge"
                :class="g.configured ? 'badge-green' : 'badge-gray'">
                {{ g.label }}: {{ g.configured ? 'configured' : 'missing keys' }}
              </span>
            </div>
          </div>

          <!-- General -->
          <div class="dash-card mb-4">
            <div class="dash-card-header">
              <h5 class="dash-card-title mb-0"><i class="ti ti-settings" style="color:#c90e0e;margin-right:0.5rem"></i>General</h5>
            </div>
            <div class="row g-3">
              <template v-for="s in settingsByGroup('general')" :key="s.key">
                <div class="col-md-6">
                  <label class="dash-field-label">{{ s.label }}</label>
                  <input v-model="settingsDraft[s.key]" type="text" class="dash-input" :placeholder="s.label" />
                </div>
              </template>
            </div>
          </div>

          <div class="d-flex gap-2">
            <button class="btn" :disabled="settingsSaving" @click="saveSettings">
              <span class="border-style">
                <i class="ti ti-device-floppy"></i>
                {{ settingsSaving ? 'Saving...' : 'Save Settings' }}
              </span>
            </button>
            <button class="dash-action-btn" @click="loadSettings">
              <i class="ti ti-refresh"></i> Reset
            </button>
          </div>

        </template>
      </div>

      <!-- MEDIA LIBRARY -->
      <div v-if="activeTab === 'media'">
        <div class="dash-page-header">
          <h2 class="dash-heading mb-0">Media Library</h2>
          <label class="btn" style="cursor:pointer">
            <span class="border-style">{{ mediaUploading ? 'Uploading...' : '+ Upload Image' }}</span>
            <input type="file" class="d-none" accept="image/*" :disabled="mediaUploading" @change="uploadMedia" />
          </label>
        </div>

        <div v-if="mediaLoading" class="dash-loading">Loading media...</div>

        <div v-else-if="!mediaList.length" class="dash-card text-center py-5">
          <i class="ti ti-photo-off" style="font-size:2.5rem;color:#555;display:block;margin-bottom:0.75rem"></i>
          <p class="text-silver mb-0">No images uploaded yet. Upload your first image above.</p>
        </div>

        <div v-else class="media-grid">
          <div v-for="item in mediaList" :key="item.id" class="media-item">
            <div class="media-thumb">
              <img :src="item.url" :alt="item.original_name" />
            </div>
            <div class="media-info">
              <div class="media-name" :title="item.original_name">{{ item.original_name }}</div>
              <div class="media-actions">
                <button class="dash-action-btn" @click="copyMediaUrl(item.url, item.id)">
                  <i :class="mediaCopied === item.id ? 'ti ti-check' : 'ti ti-copy'"></i>
                  {{ mediaCopied === item.id ? 'Copied!' : 'Copy URL' }}
                </button>
                <button class="dash-action-btn dash-action-danger" @click="deleteMedia(item.id)">
                  <i class="ti ti-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ANALYTICS -->
      <AnalyticsPanel v-if="activeTab === 'analytics'" />

      <!-- SALES GUIDE -->
      <SalesGuide v-if="activeTab === 'guide'" />

  </AdminLayout>

  <!-- Assign Line Modal -->
  <div v-if="assignModal.open" class="dash-modal-overlay" @click.self="assignModal.open = false">
    <div class="dash-modal" style="max-width:560px">
      <div class="dash-modal-header">
        <h5>IPTV Line — {{ assignModal.name }}</h5>
        <button class="dash-modal-close" @click="assignModal.open = false">✕</button>
      </div>
      <div class="dash-modal-body">
        <div v-if="assignModal.error" class="dash-alert dash-alert-danger mb-3">{{ assignModal.error }}</div>

        <div v-if="assignModal.mac_address" class="dash-mac-info mb-3">
          <i class="ti ti-device-desktop"></i>
          <div>
            <div style="color:#a0a0a0;font-size:0.75rem;margin-bottom:2px">Client MAC Address</div>
            <code style="color:#c90e0e;font-size:0.95rem">{{ assignModal.mac_address }}</code>
          </div>
        </div>
        <div v-else class="dash-mac-info dash-mac-missing mb-3">
          <i class="ti ti-device-desktop"></i>
          <span style="color:#888;font-size:0.85rem">No MAC address submitted yet</span>
        </div>

        <div v-if="assignModal.panel_type" class="dash-mac-info mb-3">
          <i class="ti ti-server"></i>
          <div>
            <div style="color:#a0a0a0;font-size:0.75rem;margin-bottom:2px">
              Provisioned on panel as <strong style="color:#a855f7;text-transform:uppercase">{{ assignModal.panel_type }}</strong>
              <span v-if="assignModal.expires_at"> · expires {{ formatDate(assignModal.expires_at) }}</span>
            </div>
            <code v-if="assignModal.portal_url" style="color:#c90e0e;font-size:0.85rem">{{ assignModal.portal_url }}</code>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-12">
            <label class="dash-field-label">Server URL</label>
            <input v-model="assignModal.form.server_url" type="text" class="dash-input" placeholder="http://yourserver.com:8080" />
          </div>
          <div class="col-12">
            <label class="dash-field-label">M3U URL</label>
            <input v-model="assignModal.form.m3u_url" type="text" class="dash-input" placeholder="http://server:port/get.php?username=...&password=...&type=m3u" />
          </div>
          <div class="col-12">
            <label class="dash-field-label">EPG URL</label>
            <input v-model="assignModal.form.epg_url" type="text" class="dash-input" placeholder="http://server:port/xmltv.php?username=...&password=..." />
          </div>
          <div class="col-6">
            <label class="dash-field-label">Username</label>
            <input v-model="assignModal.form.username" type="text" class="dash-input" />
          </div>
          <div class="col-6">
            <label class="dash-field-label">Password</label>
            <input v-model="assignModal.form.password" type="text" class="dash-input" />
          </div>
          <div class="col-12">
            <label class="dash-field-label">Notes for client (optional)</label>
            <textarea v-model="assignModal.form.notes" class="dash-input" rows="2" style="height:auto;resize:vertical" placeholder="e.g. Use iboPlayer, enter server URL above"></textarea>
          </div>
        </div>
      </div>
      <div class="dash-modal-footer" style="flex-wrap:wrap;gap:0.75rem">
        <button class="btn" :disabled="assignModal.saving" @click="submitAssign">
          <span class="border-style">{{ assignModal.saving ? 'Saving...' : 'Save & Activate' }}</span>
        </button>
        <button
          v-if="assignModal.line_id"
          class="dash-action-btn"
          style="border-color:#a855f7;color:#a855f7"
          :disabled="!!panelBusy"
          @click="provisionFromPanel"
        >
          <i class="ti ti-server"></i> {{ panelBusy === 'provision' ? 'Provisioning...' : (assignModal.panel_type ? 'Extend via Panel' : 'Auto-Provision') }}
        </button>
        <button
          v-if="assignModal.line_id && (assignModal.panel_type || assignModal.form.username || assignModal.mac_address)"
          class="dash-action-btn"
          :disabled="!!panelBusy"
          @click="refreshFromPanel"
        >
          <i class="ti ti-refresh"></i> {{ panelBusy === 'refresh' ? 'Refreshing...' : 'Refresh Status' }}
        </button>
        <button
          v-if="assignModal.line_id"
          class="dash-action-btn"
          style="border-color:#60a5fa;color:#60a5fa"
          :disabled="emailingCreds"
          @click="emailCredentials"
        >
          <i class="ti ti-mail"></i> {{ emailingCreds ? 'Sending...' : 'Email Credentials' }}
        </button>
        <button class="dash-action-btn" @click="assignModal.open = false">Cancel</button>
        <div v-if="emailCredsMsg" class="w-100" style="font-size:0.82rem;color:#4ade80;margin-top:0.25rem">
          {{ emailCredsMsg }}
        </div>
        <div v-if="panelMsg" class="w-100" style="font-size:0.82rem;color:#4ade80;margin-top:0.25rem">
          {{ panelMsg }}
        </div>
      </div>
    </div>
  </div>

  <!-- Invoice Dialog -->
  <div v-if="invoiceDialog.open" class="dash-modal-overlay" @click.self="invoiceDialog.open = false">
    <div class="dash-modal">
      <div class="dash-modal-header">
        <h5>Payment Invoice</h5>
        <button class="dash-modal-close" @click="invoiceDialog.open = false">✕</button>
      </div>
      <div class="dash-modal-body text-center" style="padding:2rem">
        <p class="text-silver mb-3" style="font-size:0.9rem">Payment pages cannot be embedded. Open in a new tab:</p>
        <a :href="invoiceDialog.url" target="_blank" rel="noopener" class="btn" @click="invoiceDialog.open = false">
          <span class="border-style"><i class="ti ti-external-link"></i> Open Invoice Page</span>
        </a>
      </div>
    </div>
  </div>

  <!-- Package Modal -->
  <div v-if="pkgModal.open" class="dash-modal-overlay" @click.self="pkgModal.open = false">
    <div class="dash-modal">
      <div class="dash-modal-header">
        <h5>{{ pkgModal.editing ? 'Edit Package' : 'New Package' }}</h5>
        <button class="dash-modal-close" @click="pkgModal.open = false">✕</button>
      </div>
      <div class="dash-modal-body">
        <div v-if="pkgModal.error" class="dash-alert dash-alert-danger mb-3">{{ pkgModal.error }}</div>
        <div class="row g-3">
          <div class="col-md-6">
            <label class="dash-field-label">Package Name</label>
            <input v-model="pkgModal.form.name" type="text" class="dash-input" />
          </div>
          <div class="col-md-3">
            <label class="dash-field-label">Price ($)</label>
            <input v-model.number="pkgModal.form.price" type="number" step="0.01" class="dash-input" />
          </div>
          <div class="col-md-3">
            <label class="dash-field-label">Days</label>
            <input v-model.number="pkgModal.form.duration_days" type="number" class="dash-input" />
          </div>
          <div class="col-12">
            <label class="dash-field-label">Package Image</label>
            <div class="pkg-image-row">
              <div v-if="pkgModal.form.image_url" class="pkg-image-preview">
                <img :src="pkgModal.form.image_url" alt="Package image" />
                <button class="pkg-image-remove" @click="pkgModal.form.image_url = ''" title="Remove image">✕</button>
              </div>
              <div class="pkg-image-upload">
                <input v-model="pkgModal.form.image_url" type="text" class="dash-input" placeholder="Paste image URL or upload below..." style="flex:1" />
                <label class="dash-action-btn" style="cursor:pointer;white-space:nowrap">
                  <i class="ti ti-upload"></i>
                  {{ pkgModal.imageUploading ? 'Uploading...' : 'Upload' }}
                  <input type="file" accept="image/*" class="d-none" :disabled="pkgModal.imageUploading" @change="uploadPkgImage" />
                </label>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <label class="dash-field-label">Panel Package (auto-provisioning)</label>
            <select v-if="panelPackages.length" v-model="pkgModal.form.panel_package_id" class="dash-input">
              <option :value="null">— Not mapped (assign lines manually) —</option>
              <option v-for="pp in panelPackages" :key="pp.id" :value="Number(pp.id)">
                #{{ pp.id }} — {{ pp.name }} ({{ pp.credits }} cr / {{ pp.duration }} {{ pp.duration_in }})
              </option>
            </select>
            <input v-else v-model.number="pkgModal.form.panel_package_id" type="number" min="1" class="dash-input" placeholder="Panel package ID (panel offline)" />
          </div>
          <div class="col-md-6">
            <label class="dash-field-label">Panel Template (optional)</label>
            <select v-if="panelTemplates.length" v-model="pkgModal.form.panel_template_id" class="dash-input">
              <option :value="null">— Default —</option>
              <option v-for="t in panelTemplates" :key="t.id" :value="Number(t.id)">
                #{{ t.id }} — {{ t.title }}
              </option>
            </select>
            <input v-else v-model.number="pkgModal.form.panel_template_id" type="number" min="1" class="dash-input" placeholder="Panel template ID (optional)" />
          </div>
          <div class="col-12">
            <label class="dash-field-label">Features (one per line)</label>
            <textarea v-model="pkgModal.featuresText" class="dash-input" rows="4" style="height:auto;resize:vertical" placeholder="HD Channels&#10;Sports Package&#10;24/7 Support"></textarea>
          </div>
        </div>
      </div>
      <div class="dash-modal-footer">
        <button class="btn" :disabled="pkgModal.saving" @click="submitPackage">
          <span class="border-style">{{ pkgModal.saving ? 'Saving...' : (pkgModal.editing ? 'Update' : 'Create') }}</span>
        </button>
        <button class="dash-action-btn" @click="pkgModal.open = false">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Bill Client Modal -->
  <div v-if="billModal.open" class="dash-modal-overlay" @click.self="billModal.open = false">
    <div class="dash-modal" style="max-width:500px">
      <div class="dash-modal-header">
        <h5><i class="ti ti-credit-card" style="color:#4ade80;margin-right:0.4rem"></i>Bill Client</h5>
        <button class="dash-modal-close" @click="billModal.open = false">✕</button>
      </div>
      <div class="dash-modal-body">
        <div v-if="billModal.error"   class="dash-alert dash-alert-danger mb-3">{{ billModal.error }}</div>
        <div v-if="billModal.success" class="dash-alert dash-alert-success mb-3">{{ billModal.success }}</div>

        <!-- User picker -->
        <div class="dash-field-group mb-3" style="position:relative">
          <label class="dash-field-label">Client</label>
          <div style="position:relative">
            <input
              v-model="billModal.userQuery"
              type="text"
              class="dash-input"
              placeholder="Search by name or email..."
              :style="billModal.user_id ? 'border-color:#4ade80' : ''"
              @input="onBillUserQuery"
              @keydown.esc="billModal.userResults = []"
              autocomplete="off"
            />
            <button v-if="billModal.user_id" class="compose-clear-btn" @click="clearBillUser" title="Clear">✕</button>
          </div>
          <div v-if="billModal.user_id" class="compose-resolved">
            <i class="ti ti-circle-check" style="color:#4ade80"></i>
            {{ billModal.userName }} — {{ billModal.userEmail }}
          </div>
          <div v-if="billModal.userResults.length" class="compose-dropdown">
            <div
              v-for="u in billModal.userResults"
              :key="u.id"
              class="compose-dropdown-item"
              @mousedown.prevent="selectBillUser(u)"
            >
              <div class="dash-mini-avatar" style="width:28px;height:28px;font-size:0.75rem;flex-shrink:0">{{ u.name?.[0]?.toUpperCase() }}</div>
              <div>
                <div style="color:#fff;font-size:0.88rem;font-weight:600">{{ u.name }}</div>
                <div style="color:#888;font-size:0.78rem">{{ u.email }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Package picker -->
        <div class="dash-field-group mb-3">
          <label class="dash-field-label">Package</label>
          <select v-model="billModal.package_id" class="dash-input" @change="onBillPackageChange">
            <option value="">— Select a package —</option>
            <option v-for="p in packages" :key="p.id" :value="p.id">
              {{ p.name }} — ${{ p.price }} / {{ p.duration_days }} days
            </option>
          </select>
        </div>

        <!-- Amount -->
        <div class="dash-field-group mb-3">
          <label class="dash-field-label">Amount Paid (USD)</label>
          <input v-model="billModal.amount" type="number" step="0.01" min="0" class="dash-input" placeholder="0.00" />
        </div>

        <!-- Notes -->
        <div class="dash-field-group">
          <label class="dash-field-label">Notes (optional)</label>
          <textarea v-model="billModal.notes" class="dash-input" rows="2" style="height:auto;resize:vertical" placeholder="e.g. Paid via M-Pesa, Ref #12345"></textarea>
        </div>
      </div>
      <div class="dash-modal-footer">
        <button class="btn" :disabled="billModal.saving || billModal.sendingLink" @click="submitBill">
          <span class="border-style">
            <i class="ti ti-circle-check"></i>
            {{ billModal.saving ? 'Activating...' : 'Activate Subscription' }}
          </span>
        </button>
        <button class="dash-action-btn" :disabled="billModal.saving || billModal.sendingLink" @click="sendPaymentLink" title="Create a Lipad card/M-Pesa payment link and email it to the client">
          <i class="ti ti-link"></i>
          {{ billModal.sendingLink ? 'Sending link...' : 'Email Payment Link' }}
        </button>
        <button class="dash-action-btn" @click="billModal.open = false">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Email Compose Modal -->
  <div v-if="emailModal.open" class="dash-modal-overlay" @click.self="emailModal.open = false">
    <div class="dash-modal" style="max-width:520px">
      <div class="dash-modal-header">
        <h5><i class="ti ti-mail" style="color:#60a5fa;margin-right:0.4rem"></i>Email Client</h5>
        <button class="dash-modal-close" @click="emailModal.open = false">✕</button>
      </div>
      <div class="dash-modal-body">
        <div v-if="emailModal.error" class="dash-alert dash-alert-danger mb-3">{{ emailModal.error }}</div>
        <div v-if="emailModal.success" class="dash-alert dash-alert-success mb-3">{{ emailModal.success }}</div>

        <div class="dash-field-group mb-3">
          <label class="dash-field-label">To</label>
          <input
            :value="emailModal.to_name + ' <' + emailModal.to_email + '>'"
            type="text"
            class="dash-input"
            readonly
            style="background:#0a0102;color:#888;cursor:default"
          />
        </div>

        <div class="dash-field-group mb-3">
          <label class="dash-field-label">Subject</label>
          <input v-model="emailModal.subject" type="text" class="dash-input" placeholder="e.g. Your GoPlayTV subscription" />
        </div>

        <div class="dash-field-group">
          <label class="dash-field-label">Message</label>
          <textarea
            v-model="emailModal.body"
            class="dash-input"
            rows="8"
            style="height:auto;resize:vertical"
            placeholder="Write your message here..."
          ></textarea>
        </div>
      </div>
      <div class="dash-modal-footer">
        <button class="btn" :disabled="emailModal.sending" @click="sendEmail">
          <span class="border-style">
            <i class="ti ti-send"></i>
            {{ emailModal.sending ? 'Sending...' : 'Send Email' }}
          </span>
        </button>
        <button class="dash-action-btn" @click="emailModal.open = false">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Channel Modal -->
  <div v-if="chModal.open" class="dash-modal-overlay" @click.self="chModal.open = false">
    <div class="dash-modal">
      <div class="dash-modal-header">
        <h5>{{ chModal.editing ? 'Edit Channel' : 'New Channel' }}</h5>
        <button class="dash-modal-close" @click="chModal.open = false">✕</button>
      </div>
      <div class="dash-modal-body">
        <div v-if="chModal.error" class="dash-alert dash-alert-danger mb-3">{{ chModal.error }}</div>
        <div class="row g-3">
          <div class="col-md-8">
            <label class="dash-field-label">Channel Name</label>
            <input v-model="chModal.form.name" type="text" class="dash-input" />
          </div>
          <div class="col-md-4">
            <label class="dash-field-label">Category</label>
            <select v-model="chModal.form.category" class="dash-input">
              <option v-for="cat in channelCategories" :key="cat">{{ cat }}</option>
            </select>
          </div>
          <div class="col-md-6">
            <label class="dash-field-label">Country (optional)</label>
            <input v-model="chModal.form.country" type="text" class="dash-input" placeholder="e.g. USA, UK, Global" />
          </div>
          <div class="col-md-3">
            <label class="dash-field-label">Sort Order</label>
            <input v-model.number="chModal.form.sort_order" type="number" class="dash-input" />
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <label class="dash-input d-flex align-items-center gap-2" style="cursor:pointer;padding:0.6rem 1rem">
              <input v-model="chModal.form.is_active" type="checkbox" style="width:auto" />
              <span style="font-size:0.85rem">Active</span>
            </label>
          </div>
          <div class="col-12">
            <label class="dash-field-label">Channel Logo</label>
            <div class="pkg-image-row">
              <div v-if="chModal.form.logo_url" class="pkg-image-preview">
                <img :src="chModal.form.logo_url" alt="Channel logo" style="object-fit:contain;background:#0a0102" />
                <button class="pkg-image-remove" @click="chModal.form.logo_url = ''">✕</button>
              </div>
              <div class="pkg-image-upload">
                <input v-model="chModal.form.logo_url" type="text" class="dash-input" placeholder="Paste logo URL or upload..." style="flex:1" />
                <label class="dash-action-btn" style="cursor:pointer;white-space:nowrap">
                  <i class="ti ti-upload"></i> {{ chModal.uploading ? 'Uploading...' : 'Upload' }}
                  <input type="file" accept="image/*" class="d-none" :disabled="chModal.uploading" @change="uploadChannelLogo" />
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="dash-modal-footer">
        <button class="btn" :disabled="chModal.saving" @click="submitChannel">
          <span class="border-style">{{ chModal.saving ? 'Saving...' : (chModal.editing ? 'Update' : 'Create') }}</span>
        </button>
        <button class="dash-action-btn" @click="chModal.open = false">Cancel</button>
      </div>
    </div>
  </div>

  <!-- Confirm / notice dialog (replaces window.confirm/alert) -->
  <div v-if="confirmDialog.open" class="dash-modal-overlay" style="z-index:1100" @click.self="resolveConfirm(false)">
    <div class="dash-modal" style="max-width:420px">
      <div class="dash-modal-body" style="padding:1.75rem">
        <p style="color:#fff;margin:0 0 1.5rem;font-size:0.95rem">{{ confirmDialog.message }}</p>
        <div class="d-flex gap-2 justify-content-end">
          <button v-if="confirmDialog.showCancel" class="dash-action-btn" @click="resolveConfirm(false)">Cancel</button>
          <button class="btn" @click="resolveConfirm(true)">
            <span class="border-style">{{ confirmDialog.okText }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script lang="ts">
import { defineComponent, ref, reactive, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import AdminLayout from "@/components/admin/AdminLayout.vue";
import SalesGuide from "@/components/admin/SalesGuide.vue";
import AnalyticsPanel from "@/components/admin/AnalyticsPanel.vue";

export default defineComponent({
  name: "AdminDashboardPage",
  components: { AdminLayout, SalesGuide, AnalyticsPanel },
  setup() {
    const route = useRoute();

    const activeTab = ref((route.query.tab as string) || "overview");

    watch(() => route.query.tab, (tab) => {
      activeTab.value = (tab as string) || "overview";
    });
    const tabs = [
      { id: "overview",  label: "Overview",    icon: "ti ti-layout-dashboard" },
      { id: "users",     label: "Users",       icon: "ti ti-users" },
      { id: "emails",    label: "Emails",      icon: "ti ti-mail" },
      { id: "payments",  label: "Payments",    icon: "ti ti-credit-card" },
      { id: "packages",  label: "Packages",    icon: "ti ti-package" },
      { id: "panel",     label: "IPTV Panel",  icon: "ti ti-server" },
      { id: "channels",  label: "Channels",    icon: "ti ti-device-tv" },
      { id: "content",   label: "Website CMS", icon: "ti ti-layout-grid" },
      { id: "media",     label: "Media",       icon: "ti ti-photo" },
      { id: "settings",  label: "Settings",    icon: "ti ti-settings" },
    ];

    // ── Stats ──────────────────────────────────────────────────────────────────
    const statsLoading = ref(true);
    const stats = ref<any>({
      total_users: 0, active_subs: 0, pending_lines: 0, total_revenue: 0,
      recent_users: [], pending_lines_list: [],
    });

    async function loadStats() {
      statsLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/dashboard-stats");
        stats.value = data;
      } finally {
        statsLoading.value = false;
      }
    }

    // ── IPTV reseller panel ────────────────────────────────────────────────────
    const panelCredits = ref<any>(null);
    const panelPackages = ref<any[]>([]);
    const panelTemplates = ref<any[]>([]);
    const panelMetaLoaded = ref(false);

    async function loadPanelCredits() {
      try {
        const { data } = await api.get("/api/admin/panel/credits");
        panelCredits.value = data.data;
      } catch {
        panelCredits.value = null; // panel not configured or unreachable — hide the card
      }
    }

    async function loadPanelMeta() {
      if (panelMetaLoaded.value) return;
      panelMetaLoaded.value = true;
      try {
        const [pkgs, tpls] = await Promise.all([
          api.get("/api/admin/panel/packages"),
          api.get("/api/admin/panel/templates"),
        ]);
        panelPackages.value = pkgs.data.data || [];
        panelTemplates.value = tpls.data.data || [];
      } catch {
        // panel not configured — modal falls back to plain numeric inputs
      }
    }

    // ── IPTV Panel tab ─────────────────────────────────────────────────────────
    const panelLinesLoading = ref(false);
    const panelLines = ref<any[]>([]);
    const panelLinesMeta = ref({ current_page: 1, last_page: 1 });
    const panelLineFilter = ref("");

    async function loadPanelLines(page = 1) {
      panelLinesLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/lines", {
          params: { page, status: panelLineFilter.value || undefined },
        });
        panelLines.value = data.data.data;
        panelLinesMeta.value = { current_page: data.data.current_page, last_page: data.data.last_page };
      } finally {
        panelLinesLoading.value = false;
      }
    }

    function mappedLocalPackage(panelId: number | string): string {
      const local = packages.value.find((p: any) => Number(p.panel_package_id) === Number(panelId));
      return local ? local.name : "";
    }

    function reloadPanelTab() {
      panelMetaLoaded.value = false;
      loadPanelCredits();
      loadPanelMeta();
      loadPanelLines(panelLinesMeta.value.current_page);
      if (!packages.value.length) loadPackages();
    }

    // ── Users ──────────────────────────────────────────────────────────────────
    const usersLoading = ref(false);
    const users = ref<any[]>([]);
    const userSearch = ref("");
    const usersMeta = ref({ current_page: 1, last_page: 1 });

    async function loadUsers(page = 1) {
      usersLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/users", { params: { page, search: userSearch.value || undefined } });
        users.value = data.data.data;
        usersMeta.value = { current_page: data.data.current_page, last_page: data.data.last_page };
      } finally {
        usersLoading.value = false;
      }
    }

    // ── Payments ──────────────────────────────────────────────────────────────
    const paymentsLoading = ref(false);
    const payments = ref<any[]>([]);
    const paymentsMeta = ref({ current_page: 1, last_page: 1 });
    const paymentStats = ref({ total: 0, completed: 0, pending: 0, failed: 0, total_revenue: 0 });
    const paymentStatusFilter = ref("");
    const markingPaid = ref<number | null>(null);
    const invoiceDialog = ref({ open: false, url: "" });

    function openInvoiceDialog(url: string) { window.open(url, '_blank', 'noopener'); }

    // In-app confirm/notice dialog — window.confirm()/alert() silently fail
    // in embedded browsers (VSCode preview, some webviews)
    const confirmDialog = reactive({
      open: false, message: "", okText: "Confirm", showCancel: true,
      _resolve: null as null | ((v: boolean) => void),
    });

    function askConfirm(message: string, okText = "Confirm"): Promise<boolean> {
      confirmDialog.message = message;
      confirmDialog.okText = okText;
      confirmDialog.showCancel = true;
      confirmDialog.open = true;
      return new Promise((resolve) => { confirmDialog._resolve = resolve; });
    }

    function notify(message: string) {
      confirmDialog.message = message;
      confirmDialog.okText = "OK";
      confirmDialog.showCancel = false;
      confirmDialog.open = true;
      confirmDialog._resolve = null;
    }

    function resolveConfirm(v: boolean) {
      confirmDialog.open = false;
      confirmDialog._resolve?.(v);
      confirmDialog._resolve = null;
    }

    async function markAsPaid(id: number) {
      if (!(await askConfirm("Mark this payment as completed and activate the subscription?", "Mark Paid"))) return;
      markingPaid.value = id;
      try {
        await api.post(`/api/admin/payments/${id}/complete`);
        await loadPayments(paymentsMeta.value.current_page);
        await loadStats();
      } catch (e: any) {
        notify(e?.response?.data?.message || "Failed to mark as paid.");
      } finally {
        markingPaid.value = null;
      }
    }

    async function loadPayments(page = 1) {
      paymentsLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/payments", {
          params: { page, status: paymentStatusFilter.value || undefined },
        });
        payments.value = data.data.data;
        paymentsMeta.value = { current_page: data.data.current_page, last_page: data.data.last_page };
        paymentStats.value = data.stats;
      } finally {
        paymentsLoading.value = false;
      }
    }

    // ── Packages ───────────────────────────────────────────────────────────────
    const pkgsLoading = ref(false);
    const packages = ref<any[]>([]);

    async function loadPackages() {
      pkgsLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/packages");
        packages.value = data.data;
      } finally {
        pkgsLoading.value = false;
      }
    }

    // ── Channels ──────────────────────────────────────────────────────────────
    const channelsLoading = ref(false);
    const channels = ref<any[]>([]);
    const channelsMeta = ref({ current_page: 1, last_page: 1 });
    const channelSearch = ref("");
    const channelCategories = ["General", "Sports", "News", "Movies", "Entertainment", "Kids", "Music", "Documentary", "Religious"];

    async function loadChannels(page = 1) {
      channelsLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/channels", { params: { page, search: channelSearch.value || undefined } });
        channels.value = data.data.data;
        channelsMeta.value = { current_page: data.data.current_page, last_page: data.data.last_page };
      } finally {
        channelsLoading.value = false;
      }
    }

    const chModal = reactive({
      open: false, editing: false, uploading: false, saving: false, error: "",
      editId: 0,
      form: { name: "", category: "General", country: "", logo_url: "", is_active: true, sort_order: 0 },
    });

    function openChannelModal(ch?: any) {
      chModal.editing = !!ch;
      chModal.editId = ch?.id ?? 0;
      chModal.form = {
        name: ch?.name ?? "",
        category: ch?.category ?? "General",
        country: ch?.country ?? "",
        logo_url: ch?.logo_url ?? "",
        is_active: ch?.is_active !== false,
        sort_order: ch?.sort_order ?? 0,
      };
      chModal.error = "";
      chModal.open = true;
    }

    async function uploadChannelLogo(event: Event) {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      chModal.uploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/api/admin/media/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        chModal.form.logo_url = data.url;
      } catch {
        chModal.error = "Logo upload failed.";
      } finally {
        chModal.uploading = false;
        (event.target as HTMLInputElement).value = "";
      }
    }

    async function submitChannel() {
      chModal.saving = true;
      chModal.error = "";
      try {
        if (chModal.editing) {
          await api.put(`/api/admin/channels/${chModal.editId}`, chModal.form);
        } else {
          await api.post("/api/admin/channels", chModal.form);
        }
        chModal.open = false;
        await loadChannels(channelsMeta.value.current_page);
      } catch (e: any) {
        chModal.error = e?.response?.data?.message || "Failed to save channel.";
      } finally {
        chModal.saving = false;
      }
    }

    async function deleteChannel(id: number) {
      if (!(await askConfirm("Delete this channel?", "Delete"))) return;
      await api.delete(`/api/admin/channels/${id}`);
      await loadChannels(channelsMeta.value.current_page);
    }

    // ── Content sections ───────────────────────────────────────────────────────
    const cmsLoading = ref(false);
    const cmsSections = ref<any[]>([]);

    async function loadCmsSections() {
      cmsLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/content");
        cmsSections.value = data.data;
      } finally {
        cmsLoading.value = false;
      }
    }

    const CMS_LABELS: Record<string, string> = {
      navigation: "Navigation & Logo", footer: "Footer",
      banner: "Main Banner", who_we_are: "Who We Are",
      features: "Our Features", our_content: "Our Content",
      favorite_match: "Favorite Match", test_service: "Test Our Service",
      benefits: "Only Benefits", testimonials: "Testimonials",
      fan_facts: "Fan Facts / Stats", faq: "FAQ",
      pricing: "Pricing Card Images",
      movies_slider: "Movies Slider",
    };

    // ── Media Library ─────────────────────────────────────────────────────────
    const mediaLoading = ref(false);
    const mediaList = ref<any[]>([]);
    const mediaCopied = ref<number | null>(null);
    const mediaUploading = ref(false);

    async function loadMedia() {
      mediaLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/media");
        mediaList.value = data.data.data ?? data.data;
      } finally {
        mediaLoading.value = false;
      }
    }

    async function deleteMedia(id: number) {
      if (!(await askConfirm("Delete this image?", "Delete"))) return;
      await api.delete(`/api/admin/media/${id}`);
      mediaList.value = mediaList.value.filter((m) => m.id !== id);
    }

    function copyMediaUrl(url: string, id: number) {
      navigator.clipboard.writeText(url);
      mediaCopied.value = id;
      setTimeout(() => (mediaCopied.value = null), 2000);
    }

    async function uploadMedia(event: Event) {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      mediaUploading.value = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/api/admin/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        mediaList.value.unshift(data);
      } catch {
        notify("Upload failed. Max 5 MB, images only.");
      } finally {
        mediaUploading.value = false;
        (event.target as HTMLInputElement).value = "";
      }
    }

    // ── Bill client modal ─────────────────────────────────────────────────────
    const billModal = reactive({
      open: false, saving: false,
      error: "", success: "",
      sendingLink: false,
      user_id: 0, userName: "", userEmail: "",
      userQuery: "", userResults: [] as any[], userSearching: false,
      package_id: "" as number | "",
      amount: "" as number | "",
      notes: "",
    });

    function openBillModal(user?: any) {
      billModal.open = true;
      billModal.saving = false;
      billModal.error = "";
      billModal.success = "";
      billModal.package_id = "";
      billModal.amount = "";
      billModal.notes = "";
      billModal.userResults = [];
      if (user) {
        billModal.user_id = user.id;
        billModal.userName = user.name;
        billModal.userEmail = user.email;
        billModal.userQuery = user.name;
      } else {
        billModal.user_id = 0;
        billModal.userName = "";
        billModal.userEmail = "";
        billModal.userQuery = "";
      }
    }

    let billUserTimer: ReturnType<typeof setTimeout> | null = null;
    async function onBillUserQuery() {
      billModal.user_id = 0;
      billModal.userName = "";
      billModal.userEmail = "";
      if (billUserTimer) clearTimeout(billUserTimer);
      if (!billModal.userQuery.trim()) { billModal.userResults = []; return; }
      billUserTimer = setTimeout(async () => {
        billModal.userSearching = true;
        try {
          const { data } = await api.get(`/api/admin/emails/search-users?q=${encodeURIComponent(billModal.userQuery)}`);
          billModal.userResults = data.users || [];
        } finally {
          billModal.userSearching = false;
        }
      }, 300);
    }

    function selectBillUser(u: any) {
      billModal.user_id = u.id;
      billModal.userName = u.name;
      billModal.userEmail = u.email;
      billModal.userQuery = u.name;
      billModal.userResults = [];
    }

    function clearBillUser() {
      billModal.user_id = 0;
      billModal.userName = "";
      billModal.userEmail = "";
      billModal.userQuery = "";
      billModal.userResults = [];
    }

    function onBillPackageChange() {
      const pkg = packages.value.find((p: any) => p.id === billModal.package_id);
      if (pkg) billModal.amount = pkg.price;
    }

    async function submitBill() {
      if (!billModal.user_id) { billModal.error = "Please select a client."; return; }
      if (!billModal.package_id) { billModal.error = "Please select a package."; return; }
      billModal.saving = true;
      billModal.error = "";
      billModal.success = "";
      try {
        const { data } = await api.post("/api/admin/billing/create", {
          user_id:    billModal.user_id,
          package_id: billModal.package_id,
          amount:     billModal.amount || undefined,
          notes:      billModal.notes || undefined,
        });
        billModal.success = data.message;
        loadUsers();
        setTimeout(() => { billModal.open = false; }, 2000);
      } catch (e: any) {
        billModal.error = e?.response?.data?.message || "Failed to activate subscription.";
      } finally {
        billModal.saving = false;
      }
    }

    async function sendPaymentLink() {
      if (!billModal.user_id) { billModal.error = "Please select a client."; return; }
      if (!billModal.package_id) { billModal.error = "Please select a package."; return; }
      billModal.sendingLink = true;
      billModal.error = "";
      billModal.success = "";
      try {
        const { data } = await api.post("/api/admin/billing/payment-link", {
          user_id:    billModal.user_id,
          package_id: billModal.package_id,
          amount:     billModal.amount || undefined,
          send_email: true,
        });
        billModal.success = data.message;
        setTimeout(() => { billModal.open = false; }, 2500);
      } catch (e: any) {
        billModal.error = e?.response?.data?.message || "Failed to create payment link.";
      } finally {
        billModal.sendingLink = false;
      }
    }

    // ── Email compose modal ────────────────────────────────────────────────────
    const emailModal = reactive({
      open: false, sending: false,
      user_id: 0, to_name: "", to_email: "",
      subject: "", body: "",
      error: "", success: "",
    });

    function openEmailModal(user: any) {
      emailModal.user_id = user.id;
      emailModal.to_name = user.name;
      emailModal.to_email = user.email;
      emailModal.subject = "";
      emailModal.body = "";
      emailModal.error = "";
      emailModal.success = "";
      emailModal.open = true;
    }

    async function sendEmail() {
      if (!emailModal.subject.trim() || !emailModal.body.trim()) {
        emailModal.error = "Subject and message are required.";
        return;
      }
      emailModal.sending = true;
      emailModal.error = "";
      emailModal.success = "";
      try {
        const { data } = await api.post(`/api/admin/users/${emailModal.user_id}/email`, {
          subject: emailModal.subject,
          body: emailModal.body,
        });
        emailModal.success = data.message;
        emailModal.subject = "";
        emailModal.body = "";
      } catch (e: any) {
        emailModal.error = e?.response?.data?.message || "Failed to send email.";
      } finally {
        emailModal.sending = false;
      }
    }

    // ── Assign modal ───────────────────────────────────────────────────────────
    const assignModal = reactive({
      open: false, saving: false, error: "",
      user_id: 0, line_id: 0, name: "", mac_address: "",
      panel_type: "", portal_url: "", expires_at: "",
      form: { server_url: "", m3u_url: "", epg_url: "", username: "", password: "", notes: "" },
    });
    const emailingCreds = ref(false);
    const emailCredsMsg = ref("");
    const panelBusy = ref("");
    const panelMsg = ref("");

    function applyLineToModal(line: any) {
      if (!line) return;
      assignModal.line_id = line.id ?? assignModal.line_id;
      assignModal.mac_address = line.mac_address ?? "";
      assignModal.panel_type = line.panel_type ?? "";
      assignModal.portal_url = line.portal_url ?? "";
      assignModal.expires_at = line.expires_at ?? "";
      assignModal.form = {
        server_url: line.server_url ?? "",
        m3u_url:    line.m3u_url    ?? "",
        epg_url:    line.epg_url    ?? "",
        username:   line.username   ?? "",
        password:   line.password   ?? "",
        notes:      line.notes      ?? "",
      };
    }

    async function openAssignModal(item: any) {
      assignModal.user_id = item.user_id ?? item.id;
      assignModal.line_id = 0;
      assignModal.name = item.name;
      assignModal.mac_address = item.mac_address ?? "";
      assignModal.panel_type = "";
      assignModal.portal_url = "";
      assignModal.expires_at = "";
      assignModal.error = "";
      emailCredsMsg.value = "";
      panelMsg.value = "";
      assignModal.form = { server_url: "", m3u_url: "", epg_url: "", username: "", password: "", notes: "" };
      try {
        const { data } = await api.get(`/api/admin/users/${assignModal.user_id}`);
        applyLineToModal(data.user?.iptv_lines?.[0]);
      } catch { /* use empty form */ }
      assignModal.open = true;
    }

    async function provisionFromPanel() {
      if (!assignModal.line_id) return;
      panelBusy.value = "provision";
      panelMsg.value = "";
      assignModal.error = "";
      try {
        const { data } = await api.post(`/api/admin/lines/${assignModal.line_id}/provision`);
        applyLineToModal(data.line);
        panelMsg.value = data.message || "Line provisioned.";
        await loadStats();
        loadPanelCredits();
      } catch (e: any) {
        assignModal.error = e?.response?.data?.message || "Provisioning failed.";
      } finally {
        panelBusy.value = "";
      }
    }

    async function refreshFromPanel() {
      if (!assignModal.line_id) return;
      panelBusy.value = "refresh";
      panelMsg.value = "";
      assignModal.error = "";
      try {
        const { data } = await api.post(`/api/admin/lines/${assignModal.line_id}/refresh`);
        applyLineToModal(data.line);
        panelMsg.value = "Status refreshed from panel.";
      } catch (e: any) {
        assignModal.error = e?.response?.data?.message || "Refresh failed.";
      } finally {
        panelBusy.value = "";
      }
    }

    async function submitAssign() {
      assignModal.saving = true;
      assignModal.error = "";
      try {
        const { data } = await api.post("/api/admin/lines/assign", { user_id: assignModal.user_id, ...assignModal.form, status: "active" });
        if (data.line?.id) assignModal.line_id = data.line.id;
        assignModal.open = false;
        await loadStats();
        if (activeTab.value === "users") await loadUsers();
      } catch (e: any) {
        assignModal.error = e?.response?.data?.message || "Failed to assign line.";
      } finally {
        assignModal.saving = false;
      }
    }

    async function emailCredentials() {
      if (!assignModal.line_id) return;
      emailingCreds.value = true;
      emailCredsMsg.value = "";
      try {
        const { data } = await api.post(`/api/admin/lines/${assignModal.line_id}/email-credentials`);
        emailCredsMsg.value = data.message;
      } catch (e: any) {
        emailCredsMsg.value = e?.response?.data?.message || "Failed to send email.";
      } finally {
        emailingCreds.value = false;
      }
    }

    // ── Package modal ──────────────────────────────────────────────────────────
    const pkgModal = reactive({
      open: false, saving: false, editing: false, imageUploading: false, error: "",
      editId: 0,
      form: {
        name: "", price: 0, duration_days: 30, image_url: "",
        panel_package_id: null as number | null,
        panel_template_id: null as number | null,
      },
      featuresText: "",
    });

    function openPackageModal(pkg?: any) {
      pkgModal.editing = !!pkg;
      pkgModal.editId = pkg?.id ?? 0;
      pkgModal.form = {
        name: pkg?.name ?? "",
        price: pkg?.price ?? 0,
        duration_days: pkg?.duration_days ?? 30,
        image_url: pkg?.image_url ?? "",
        panel_package_id: pkg?.panel_package_id ?? null,
        panel_template_id: pkg?.panel_template_id ?? null,
      };
      pkgModal.featuresText = Array.isArray(pkg?.features) ? pkg.features.join("\n") : "";
      pkgModal.error = "";
      pkgModal.open = true;
      loadPanelMeta();
    }

    async function uploadPkgImage(event: Event) {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;
      pkgModal.imageUploading = true;
      const form = new FormData();
      form.append("file", file);
      try {
        const { data } = await api.post("/api/admin/media/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        pkgModal.form.image_url = data.url;
      } catch {
        pkgModal.error = "Image upload failed. Max 5 MB, images only.";
      } finally {
        pkgModal.imageUploading = false;
        (event.target as HTMLInputElement).value = "";
      }
    }

    async function submitPackage() {
      pkgModal.saving = true;
      pkgModal.error = "";
      const features = pkgModal.featuresText.split("\n").map((f) => f.trim()).filter(Boolean);
      const payload = { ...pkgModal.form, features };
      try {
        if (pkgModal.editing) {
          await api.put(`/api/admin/packages/${pkgModal.editId}`, payload);
        } else {
          await api.post("/api/admin/packages", payload);
        }
        pkgModal.open = false;
        await loadPackages();
      } catch (e: any) {
        pkgModal.error = e?.response?.data?.message || "Failed to save package.";
      } finally {
        pkgModal.saving = false;
      }
    }

    async function deletePackage(id: number) {
      if (!(await askConfirm("Delete this package?", "Delete"))) return;
      await api.delete(`/api/admin/packages/${id}`);
      await loadPackages();
    }

    // ── Lifecycle ──────────────────────────────────────────────────────────────
    onMounted(() => {
      loadStats();
      loadPanelCredits();
      // Load data for the tab we landed on directly (watch only fires on change)
      onTabChange(activeTab.value);
    });

    // Load tab data on first visit
    // ── Email log (Emails tab) ─────────────────────────────────────────────────
    const emailsLoading = ref(false);
    const emailLog = ref<any[]>([]);
    const emailLogMeta = ref({ current_page: 1, last_page: 1 });
    const emailLogSearch = ref("");

    async function loadEmails(page = 1) {
      emailsLoading.value = true;
      try {
        const { data } = await api.get("/api/admin/emails", {
          params: { page, search: emailLogSearch.value || undefined },
        });
        emailLog.value = data.data.data;
        emailLogMeta.value = { current_page: data.data.current_page, last_page: data.data.last_page };
      } finally {
        emailsLoading.value = false;
      }
    }

    // Compose modal (full-page Emails tab version)
    const compose = reactive({
      open: false, sending: false,
      to_email: "", to_name: "", user_id: null as number | null,
      subject: "", body: "",
      error: "", success: "",
      userQuery: "", userResults: [] as any[], userSearching: false,
    });

    function openCompose(prefill?: { email: string; name: string; user_id?: number }) {
      compose.to_email   = prefill?.email   ?? "";
      compose.to_name    = prefill?.name    ?? "";
      compose.user_id    = prefill?.user_id ?? null;
      compose.subject    = "";
      compose.body       = "";
      compose.error      = "";
      compose.success    = "";
      compose.userQuery  = prefill ? `${prefill.name} <${prefill.email}>` : "";
      compose.userResults = [];
      compose.open = true;
    }

    let userSearchTimer: ReturnType<typeof setTimeout> | null = null;
    async function onUserQuery() {
      const q = compose.userQuery.trim();
      compose.user_id = null;
      compose.to_email = "";
      compose.to_name = "";
      if (q.length < 2) { compose.userResults = []; return; }
      compose.userSearching = true;
      if (userSearchTimer) clearTimeout(userSearchTimer);
      userSearchTimer = setTimeout(async () => {
        try {
          const { data } = await api.get("/api/admin/emails/search-users", { params: { q } });
          compose.userResults = data.data;
        } finally {
          compose.userSearching = false;
        }
      }, 300);
    }

    function selectUser(u: any) {
      compose.user_id    = u.id;
      compose.to_email   = u.email;
      compose.to_name    = u.name;
      compose.userQuery  = `${u.name} <${u.email}>`;
      compose.userResults = [];
    }

    function clearUser() {
      compose.user_id    = null;
      compose.to_email   = "";
      compose.to_name    = "";
      compose.userQuery  = "";
      compose.userResults = [];
    }

    async function submitCompose() {
      // Allow free-form email if no user selected — parse from userQuery field
      if (!compose.to_email) {
        const raw = compose.userQuery.trim();
        // Accept bare email or "Name <email>" format
        const match = raw.match(/<?([^\s<>]+@[^\s<>]+)>?$/);
        if (match) {
          compose.to_email = match[1];
        } else {
          compose.error = "Enter a valid email address or select a user.";
          return;
        }
      }
      if (!compose.subject.trim() || !compose.body.trim()) {
        compose.error = "Subject and message are required.";
        return;
      }
      compose.sending = true;
      compose.error = "";
      compose.success = "";
      try {
        const { data } = await api.post("/api/admin/emails/compose", {
          to_email: compose.to_email,
          to_name:  compose.to_name || undefined,
          user_id:  compose.user_id || undefined,
          subject:  compose.subject,
          body:     compose.body,
        });
        compose.success = data.message;
        compose.subject = "";
        compose.body = "";
        compose.to_email = "";
        compose.to_name = "";
        compose.user_id = null;
        compose.userQuery = "";
        await loadEmails(1);
      } catch (e: any) {
        compose.error = e?.response?.data?.message || "Failed to send email.";
      } finally {
        compose.sending = false;
      }
    }

    // ── Settings ───────────────────────────────────────────────────────────────
    const settingsLoaded = ref(false);
    const settingsLoading = ref(false);
    const settingsSaving = ref(false);
    const settingsMsg = ref("");
    const settingsError = ref("");
    const settingsRows = ref<any[]>([]);
    const settingsDraft = ref<Record<string, string>>({});
    const testMailTo = ref("");
    const testMailSending = ref(false);
    const testMailMsg = ref("");
    const testMailError = ref("");

    // Card gateways (Lipad, PayEra, …) for the Payments settings selector
    const gateways = ref<any[]>([]);
    async function loadGateways() {
      try {
        const { data } = await api.get("/api/admin/payments/gateways");
        gateways.value = data.data || [];
      } catch {
        gateways.value = [];
      }
    }

    async function loadSettings() {
      settingsLoading.value = true;
      try {
        const [{ data }] = await Promise.all([
          api.get("/api/admin/settings"),
          loadGateways(),
        ]);
        settingsRows.value = data.data;
        const draft: Record<string, string> = {};
        data.data.forEach((s: any) => { draft[s.key] = s.value ?? ""; });
        settingsDraft.value = draft;
        settingsLoaded.value = true;
      } finally {
        settingsLoading.value = false;
      }
    }

    async function saveSettings() {
      settingsSaving.value = true;
      settingsMsg.value = "";
      settingsError.value = "";
      try {
        const settings = Object.entries(settingsDraft.value).map(([key, value]) => ({ key, value }));
        await api.post("/api/admin/settings", { settings });
        settingsMsg.value = "Settings saved successfully.";
        // Reload so masked secrets refresh
        await loadSettings();
      } catch (e: any) {
        settingsError.value = e?.response?.data?.message || "Failed to save settings.";
      } finally {
        settingsSaving.value = false;
      }
    }

    async function sendTestMail() {
      if (!testMailTo.value) return;
      testMailSending.value = true;
      testMailMsg.value = "";
      testMailError.value = "";
      try {
        const { data } = await api.post("/api/admin/settings/test-mail", { to: testMailTo.value });
        testMailMsg.value = data.message;
      } catch (e: any) {
        testMailError.value = e?.response?.data?.message || "Test failed.";
      } finally {
        testMailSending.value = false;
      }
    }

    function settingsByGroup(group: string) {
      return settingsRows.value.filter(s => s.group === group);
    }

    function onTabChange(tab: string) {
      if (tab === "users" && !users.value.length) loadUsers();
      if (tab === "emails" && !emailLog.value.length) loadEmails();
      if (tab === "payments" && !payments.value.length) loadPayments();
      if (tab === "packages" && !packages.value.length) loadPackages();
      if (tab === "panel") {
        loadPanelMeta();
        if (!panelLines.value.length) loadPanelLines();
        if (!packages.value.length) loadPackages();
      }
      if (tab === "channels" && !channels.value.length) loadChannels();
      if (tab === "content" && !cmsSections.value.length) loadCmsSections();
      if (tab === "media" && !mediaList.value.length) loadMedia();
      if (tab === "settings" && !settingsLoaded.value) loadSettings();
    }

    function formatDate(iso: string) {
      return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    }

    // API returns decimal sums (e.g. revenue) as strings — coerce before formatting.
    function money(value: unknown): string {
      return (Number(value) || 0).toFixed(2);
    }

    function cmsIcon(section: string): string {
      const map: Record<string, string> = {
        navigation: "ti ti-menu-2", footer: "ti ti-layout-bottombar",
        banner: "ti ti-photo", who_we_are: "ti ti-users",
        features: "ti ti-star", our_content: "ti ti-device-tv",
        favorite_match: "ti ti-trophy", test_service: "ti ti-test-pipe",
        benefits: "ti ti-thumb-up", testimonials: "ti ti-message-circle",
        fan_facts: "ti ti-chart-bar", faq: "ti ti-help-circle",
        pricing: "ti ti-credit-card",
        movies_slider: "ti ti-movie",
      };
      return map[section] ?? "ti ti-file-text";
    }

    watch(activeTab, (tab: string) => onTabChange(tab));

    return {
      activeTab, tabs,
      statsLoading, stats,
      usersLoading, users, userSearch, usersMeta, loadUsers,
      paymentsLoading, payments, paymentsMeta, paymentStats, paymentStatusFilter, loadPayments,
      markingPaid, markAsPaid, invoiceDialog, openInvoiceDialog,
      confirmDialog, resolveConfirm,
      pkgsLoading, packages,
      cmsLoading, cmsSections, CMS_LABELS,
      mediaLoading, mediaList, mediaCopied, mediaUploading,
      loadMedia, deleteMedia, copyMediaUrl, uploadMedia,
      emailsLoading, emailLog, emailLogMeta, emailLogSearch, loadEmails,
      compose, openCompose, onUserQuery, selectUser, clearUser, submitCompose,
      billModal, openBillModal, onBillUserQuery, selectBillUser, clearBillUser, onBillPackageChange, submitBill, sendPaymentLink,
      emailModal, openEmailModal, sendEmail,
      assignModal, openAssignModal, submitAssign, emailingCreds, emailCredsMsg, emailCredentials,
      panelCredits, panelPackages, panelTemplates, panelBusy, panelMsg, provisionFromPanel, refreshFromPanel,
      panelLinesLoading, panelLines, panelLinesMeta, panelLineFilter, loadPanelLines, mappedLocalPackage, reloadPanelTab,
      channelsLoading, channels, channelsMeta, channelSearch, channelCategories, loadChannels,
      chModal, openChannelModal, uploadChannelLogo, submitChannel, deleteChannel,
      pkgModal, openPackageModal, uploadPkgImage, submitPackage, deletePackage,
      settingsLoaded, settingsLoading, settingsSaving, settingsMsg, settingsError,
      settingsRows, settingsDraft, settingsByGroup, loadSettings, saveSettings,
      gateways,
      testMailTo, testMailSending, testMailMsg, testMailError, sendTestMail,
      formatDate, money, cmsIcon,
    };
  },
});
</script>

<style scoped>

.dash-heading { color: #fff; font-size: 1.5rem; font-weight: 800; margin-bottom: 1.75rem; }
.dash-loading { color: #c0c0c0; padding: 2rem 0; }

.dash-page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.75rem;
  gap: 1rem;
  flex-wrap: wrap;
}

/* Stats */
.dash-stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.dash-stat-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}
.dash-stat-icon {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.3rem;
}
.dash-stat-value { color: #fff; font-weight: 700; font-size: 1.2rem; line-height: 1.2; }
.dash-stat-label { color: #c0c0c0; font-size: 0.78rem; margin-top: 2px; }

/* Cards */
.dash-card {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}
.dash-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.dash-card-title { color: #fff; font-weight: 700; font-size: 1rem; }
.dash-link-btn {
  background: transparent;
  border: none;
  color: #c90e0e;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
  font-family: inherit;
}
.dash-link-btn:hover { text-decoration: underline; }

/* Table */
.dash-table-wrap { overflow-x: auto; }
.dash-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;
}
.dash-table th {
  text-align: left;
  padding: 0.5rem 0.75rem;
  color: #c0c0c0;
  font-weight: 600;
  border-bottom: 1px solid #3a2526;
  white-space: nowrap;
}
.dash-table td {
  padding: 0.65rem 0.75rem;
  border-bottom: 1px solid #2b1e1f;
  vertical-align: middle;
}
.dash-table tr:last-child td { border-bottom: none; }

.dash-user-cell {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
}
.dash-mini-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #c90e0e;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Badges */
.dash-badge {
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 99px;
  text-transform: capitalize;
  white-space: nowrap;
}
.badge-green  { background: rgba(25,135,84,0.2);  color: #4ade80; }
.badge-red    { background: rgba(201,14,14,0.2);   color: #f87171; }
.badge-orange { background: rgba(255,193,7,0.2);   color: #fbbf24; }
.badge-gray   { background: rgba(192,192,192,0.1); color: #9ca3af; }

/* Pending item */
.dash-pending-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #3a2526;
}
.dash-pending-item:last-child { border-bottom: none; }

/* Action buttons */
.dash-action-btn {
  background: transparent;
  border: 1px solid #3a2526;
  border-radius: 6px;
  color: #c0c0c0;
  cursor: pointer;
  padding: 0.3rem 0.75rem;
  font-size: 0.8rem;
  font-family: inherit;
  transition: all 0.2s;
  white-space: nowrap;
}
.dash-action-btn:hover { border-color: #c90e0e; color: #c90e0e; }
.dash-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.dash-action-danger { border-color: #3a2526; }
.dash-action-danger:hover { border-color: #dc3545; color: #f87171; background: rgba(220,53,69,0.1); }
.active-filter { border-color: #c90e0e; color: #c90e0e; }

/* Package table thumbnail */
.pkg-thumb-wrap { width: 52px; height: 40px; border-radius: 6px; overflow: hidden; border: 1px solid #3a2526; }
.pkg-thumb { width: 100%; height: 100%; object-fit: cover; display: block; }
.pkg-thumb-empty {
  width: 100%; height: 100%;
  background: #0a0102;
  display: flex; align-items: center; justify-content: center;
  color: #3a2526; font-size: 1rem;
}

/* Package image upload */
.pkg-image-row { display: flex; flex-direction: column; gap: 0.5rem; }
.pkg-image-upload { display: flex; gap: 0.5rem; align-items: center; }
.pkg-image-preview {
  position: relative;
  width: 100%;
  max-width: 180px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #3a2526;
}
.pkg-image-preview img { width: 100%; display: block; aspect-ratio: 4/3; object-fit: cover; }
.pkg-image-remove {
  position: absolute; top: 4px; right: 4px;
  background: rgba(0,0,0,0.7); border: none; color: #fff;
  border-radius: 4px; cursor: pointer; font-size: 0.75rem; padding: 2px 6px;
}

/* Search input */
.dash-search { max-width: 280px; }

/* Inputs */
.dash-field-label { color: #c0c0c0; font-size: 0.82rem; display: block; margin-bottom: 0.35rem; }
.dash-input {
  width: 100%;
  background: #0a0102;
  border: 1px solid #3a2526;
  border-radius: 8px;
  color: #fff;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  font-family: inherit;
  outline: none;
  transition: border-color 0.2s;
}
.dash-input:focus { border-color: #c90e0e; }

/* Alerts */
.dash-alert { padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; }
.dash-alert-success { background: rgba(25,135,84,0.15);  color: #4ade80; border: 1px solid rgba(25,135,84,0.3); }
.dash-alert-danger  { background: rgba(201,14,14,0.15);  color: #f87171; border: 1px solid rgba(201,14,14,0.3); }

/* Modal */
.dash-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.dash-modal {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 16px;
  width: 100%;
  max-width: 540px;
  max-height: 90vh;
  overflow-y: auto;
}
.dash-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #3a2526;
}
.dash-modal-header h5 { color: #fff; margin: 0; font-size: 1rem; font-weight: 700; }
.dash-modal-close {
  background: transparent;
  border: none;
  color: #c0c0c0;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
}
.dash-modal-body { padding: 1.5rem; }
.dash-modal-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid #3a2526;
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

/* MAC address info in assign modal */
.dash-mac-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(201,14,14,0.08);
  border: 1px solid rgba(201,14,14,0.25);
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
.dash-mac-info i { color: #c90e0e; font-size: 1.2rem; flex-shrink: 0; }
.dash-mac-missing {
  background: rgba(255,255,255,0.03);
  border-color: #3a2526;
}
.dash-mac-missing i { color: #555; }

/* Media Library */
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}
.media-item {
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 10px;
  overflow: hidden;
}
.media-thumb {
  width: 100%;
  aspect-ratio: 4/3;
  background: #0a0102;
  overflow: hidden;
}
.media-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.media-info {
  padding: 0.6rem 0.75rem;
}
.media-name {
  color: #c0c0c0;
  font-size: 0.78rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.5rem;
}
.media-actions {
  display: flex;
  gap: 0.4rem;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-layout { flex-direction: column; }
  .dash-sidebar {
    width: 100%;
    height: auto;
    position: static;
    padding: 0.75rem;
  }
  .dash-brand { display: none; }
  .dash-nav { flex-direction: row; flex-wrap: wrap; }
  .dash-main { padding: 1.5rem 1rem; }
}

/* Settings */
.st-secret-badge {
  display: inline-block;
  background: rgba(251,191,36,0.15);
  color: #fbbf24;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 1px 6px;
  border-radius: 99px;
  border: 1px solid rgba(251,191,36,0.3);
  margin-left: 0.4rem;
  vertical-align: middle;
  text-transform: uppercase;
}
.st-test-row {
  border-top: 1px solid #3a2526;
  padding-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.st-test-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #a0a0a0;
  font-size: 0.85rem;
}
.st-test-inputs {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  align-items: center;
}
.st-test-result { font-size: 0.82rem; }
.st-ok  { color: #4ade80; }
.st-err { color: #f87171; }

/* Compose */
.compose-to-row {
  position: relative;
  display: flex;
  align-items: center;
}
.compose-clear-btn {
  position: absolute;
  right: 0.75rem;
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 0.85rem;
  line-height: 1;
}
.compose-resolved {
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #4ade80;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.compose-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0; right: 0;
  z-index: 999;
  background: #201516;
  border: 1px solid #3a2526;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.5);
}
.compose-dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 1rem;
  cursor: pointer;
  transition: background 0.15s;
}
.compose-dropdown-item:hover { background: rgba(201,14,14,0.12); }
.dash-field-group { display: flex; flex-direction: column; gap: 0.35rem; }
</style>
